import {
    Component,
    OnInit,
    Input,
    ViewChild,
    ViewContainerRef,
    SimpleChanges,
} from '@angular/core';
import { UtilService } from 'src/app/admin/core/services/utilService';
import { DynamicDocumentFieldInfoType } from 'src/app/shared/entities/dynamic-document';
import { Language } from 'src/app/shared/entities/language';
import { TableService } from 'src/app/shared/services/table.service';

@Component({
    selector: 'app-dynamic-table-column',
    templateUrl: './dynamic-table-row.component.html',
    styleUrls: ['./dynamic-table-row.component.css'],
})
export class DynamicTableRowComponent implements OnInit {
    @Input() public isEditMode: boolean = false;
    @Input() public columnDefs: any;
    @Input() public columnData: any;
    @Input() public columnName: any;
    @Input() public languages: Language[];
    @Input() public entite: any;
    @Input() public elementdata: any;
    columnDef: any;

    @ViewChild('vcr', { static: true, read: ViewContainerRef })
    vcr!: ViewContainerRef;

    viewRefrence: any;
    fieldConfig: any;
    private lastLoadedData: any = null; // Track last successfully loaded data
    private isLinkType: boolean = false; // Track if this is a link type column
    private isLoading: boolean = false; // Track if component is currently loading
    private lastProcessedData: any = null; // Track last processed data to avoid duplicate loads

    constructor(private tableService: TableService, private utilService: UtilService) {}

    ngOnInit(): void {}

    async ngOnChanges(changes: SimpleChanges) {
        // Early return: Only process if columnData, columnDefs, or columnName actually changed
        // This prevents unnecessary processing when other inputs change
        const hasRelevantChange =
            changes['columnData'] ||
            changes['columnDefs'] ||
            changes['columnName'] ||
            changes['languages'] ||
            changes['entite'] ||
            changes['elementdata'];

        if (!hasRelevantChange) {
            return;
        }

        if (changes['columnDefs']) {
            this.columnDefs = changes['columnDefs'].currentValue;
        }
        if (changes['columnData']) {
            this.columnData = changes['columnData'].currentValue;
        }
        if (changes['columnName']) {
            this.columnName = changes['columnName'].currentValue;
        }

        // CRITICAL: Early check - Skip if columnData hasn't actually changed
        // This prevents unnecessary re-renders and improves performance
        if (changes['columnData']) {
            const newDataStringified = JSON.stringify(this.columnData);
            const oldDataStringified = changes['columnData'].previousValue
                ? JSON.stringify(changes['columnData'].previousValue)
                : null;

            // If data hasn't changed and we have processed it before, skip processing
            if (
                newDataStringified === oldDataStringified &&
                this.lastProcessedData === newDataStringified &&
                (this.viewRefrence || (this.vcr && this.vcr.length > 0))
            ) {
                return; // Skip processing if data hasn't changed
            }
        }

        this.columnDef = this.columnDefs?.[this.columnName];

        try {
            if (!this.columnDef) {
                this.columnDef = Array.isArray(this.columnDefs)
                    ? this.columnDefs.find((el: any) => el.name === this.columnName)
                    : null;
            }
        } catch {}

        // Check if this is a link type
        this.isLinkType =
            this.columnDef &&
            (this.columnDef?.type === DynamicDocumentFieldInfoType.DOCUMENT_LINKS ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.DOCUMENT_LINK ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.PARAMETER_LINKS ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.PARAMETER_LINK);

        if (
            this.columnDef &&
            (this.columnDef?.type === 'picto' ||
                this.columnDef?.type === 'icon' ||
                this.columnDef?.type === 'object' ||
                this.columnDef?.type === 'imagesmap' ||
                this.columnDef?.type === 'multilangue' ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.DATE_TIME ||
                (this.columnDef?.type === DynamicDocumentFieldInfoType.TEXT &&
                    this.columnDef.isMultilingual) ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.DOCUMENT_LINK ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.DOCUMENT_LINKS ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.PARAMETER_LINK ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.PARAMETER_LINKS ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.RESOURCE_FILES ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.FORMATTED_TEXT ||
                this.columnDef?.type === DynamicDocumentFieldInfoType.MULTILANGUE_FORMATTED_TEXT ||
                this.columnDef?.type === 'label' ||
                this.columnDef?.type === 'actionbutton' ||
                this.columnDef?.type === 'linkbutton' ||
                this.columnDef?.type === 'color')
        ) {
            this.fieldConfig = this.tableService.getColumnConfig(
                this.columnDef,
                this.columnData,
                this.languages
            );

            // For link types, prevent loading with empty data
            // This handles the case when ngOnChanges is called multiple times during navigation/visualisation change
            if (this.isLinkType) {
                const currentDataIsEmpty =
                    !this.columnData ||
                    (Array.isArray(this.columnData) && this.columnData.length === 0) ||
                    this.columnData === null ||
                    this.columnData === undefined;

                const currentDataHasContent =
                    Array.isArray(this.columnData) && this.columnData.length > 0;

                // Case 1: If we already have a view reference and valid data loaded, skip reload with empty data
                if (this.viewRefrence && this.lastLoadedData) {
                    const lastDataWasValid =
                        Array.isArray(this.lastLoadedData) && this.lastLoadedData.length > 0;

                    if (currentDataIsEmpty && lastDataWasValid && !currentDataHasContent) {
                        return; // Don't reload with empty data if we already have valid data
                    }
                }

                // Case 2: If this is first load (no viewRefrence) and data is empty, skip loading
                // Wait for valid data to arrive
                if (!this.viewRefrence && currentDataIsEmpty && !currentDataHasContent) {
                    return; // Don't load with empty data on first load, wait for valid data
                }
            }

            if (this.fieldConfig != null && this.fieldConfig != undefined && this.vcr) {
                // Prevent multiple simultaneous loads
                if (this.isLoading) {
                    return;
                }

                // Check if data actually changed (avoid reloading with same data)
                // Check even if viewRefrence is null (component might have been recreated but vcr still has component)
                const dataStringified = JSON.stringify(this.columnData);
                const hasComponent = this.viewRefrence || (this.vcr && this.vcr.length > 0);
                if (this.lastProcessedData === dataStringified && hasComponent) {
                    return;
                }

                this.fieldConfig.entite = this.entite;
                this.fieldConfig.columnName = this.columnName;
                this.fieldConfig.columnData = this.columnData;
                this.fieldConfig.elementdata = this.elementdata;

                // For link types: Check if we should skip reload
                if (this.isLinkType) {
                    const currentDataIsEmpty =
                        !this.columnData ||
                        (Array.isArray(this.columnData) && this.columnData.length === 0) ||
                        this.columnData === null ||
                        this.columnData === undefined;
                    const currentDataHasContent =
                        Array.isArray(this.columnData) && this.columnData.length > 0;

                    // Check if vcr already has a component (even if viewRefrence is null due to recreation)
                    const hasExistingComponent = this.vcr.length > 0;

                    // Try to get existing component's data if available
                    let existingComponentHasData = false;
                    if (hasExistingComponent) {
                        try {
                            const existingComponent = this.vcr.get(0);
                            if (existingComponent) {
                                const instance = (existingComponent as any)?.instance;
                                const configValue =
                                    instance?.config?.value || instance?.config?.columnData;
                                existingComponentHasData =
                                    Array.isArray(configValue) && configValue.length > 0;
                            }
                        } catch (e) {
                            // Ignore errors when accessing component
                        }
                    }

                    // Check lastLoadedData even if viewRefrence is null (component might have been recreated)
                    const lastDataHasContent =
                        this.lastLoadedData &&
                        Array.isArray(this.lastLoadedData) &&
                        this.lastLoadedData.length > 0;

                    // CRITICAL: Skip reload if current data is empty AND we have valid lastLoadedData
                    // This prevents clearing component with valid data when ngOnChanges is called with null/empty data
                    // This works even when component is recreated because lastLoadedData persists
                    if (currentDataIsEmpty && lastDataHasContent && !currentDataHasContent) {
                        return; // Don't reload with empty data if we have valid lastLoadedData
                    }

                    // Skip reload if:
                    // 1. Current data is empty AND existing component has data (don't overwrite valid data with empty)
                    // 2. OR if we have viewRefrence and lastLoadedData, check if data changed
                    if (this.viewRefrence && this.lastLoadedData) {
                        const dataChanged =
                            JSON.stringify(this.columnData) !== JSON.stringify(this.lastLoadedData);
                        const oldDataHasContent =
                            Array.isArray(this.lastLoadedData) && this.lastLoadedData.length > 0;

                        if (!dataChanged || (!currentDataHasContent && oldDataHasContent)) {
                            return;
                        }
                    } else if (
                        hasExistingComponent &&
                        existingComponentHasData &&
                        currentDataIsEmpty
                    ) {
                        // Component was recreated but vcr still has component with data
                        // Don't overwrite with empty data
                        return;
                    }
                }

                // Set loading flag
                this.isLoading = true;

                try {
                    // For link types: Don't clear if we're about to load with empty data and component already has data
                    let shouldClear = true;
                    // Check vcr.length even if viewRefrence is null (component might have been recreated)
                    if (this.isLinkType && this.vcr && this.vcr.length > 0) {
                        const currentDataIsEmpty =
                            !this.columnData ||
                            (Array.isArray(this.columnData) && this.columnData.length === 0) ||
                            this.columnData === null ||
                            this.columnData === undefined;

                        if (currentDataIsEmpty) {
                            // Check if existing component has data
                            try {
                                const existingComponent = this.vcr.get(0);
                                if (existingComponent) {
                                    const instance = (existingComponent as any)?.instance;
                                    const configValue =
                                        instance?.config?.value || instance?.config?.columnData;
                                    const hasData =
                                        Array.isArray(configValue) && configValue.length > 0;

                                    if (hasData) {
                                        // Don't clear - keep existing component with data
                                        shouldClear = false;
                                    }
                                }
                            } catch (e) {
                                // Ignore errors
                            }
                        }
                    }

                    // Destroy old view reference before creating new one
                    if (shouldClear && this.viewRefrence && this.vcr) {
                        try {
                            this.vcr.clear();
                        } catch (e) {
                            // Ignore errors
                        }
                    }

                    // Only load if we cleared or if there's no existing component
                    if (shouldClear || !this.viewRefrence || this.vcr.length === 0) {
                        const loadedComponent = await this.tableService.loadColumn(
                            this.vcr,
                            this.fieldConfig,
                            this.columnDef.type
                        );

                        // If loadColumn returns null, it means we kept the existing component
                        // In that case, don't update viewRefrence
                        if (loadedComponent !== null) {
                            this.viewRefrence = loadedComponent;
                        }
                    }

                    // Track successfully loaded data for link types
                    // Set lastLoadedData even if viewRefrence is null (component might have been recreated)
                    // This ensures we can prevent data loss even after component recreation
                    if (this.isLinkType) {
                        const hasContent =
                            Array.isArray(this.columnData) && this.columnData.length > 0;
                        if (hasContent) {
                            this.lastLoadedData = this.columnData;
                        }
                    }

                    // Track processed data to avoid duplicate loads
                    this.lastProcessedData = JSON.stringify(this.columnData);
                } finally {
                    // Always reset loading flag
                    this.isLoading = false;
                }
            }
        } else if (
            typeof this.columnData === 'string' ||
            typeof this.columnData === 'number' ||
            typeof this.columnData === 'boolean'
        ) {
            this.fieldConfig = this.tableService.getColumnConfigPrimitive(this.columnData);

            // if (typeof this.columnData === 'boolean') {
            //   this.fieldConfig = { value: this.columnData };
            // }

            if (this.fieldConfig != null && this.fieldConfig != undefined && this.vcr) {
                this.fieldConfig.columnName = this.columnName;
                this.fieldConfig.columnData = this.columnData;
                this.fieldConfig.elementdata = this.elementdata;
                if (this.columnName === 'nextExecutionTime') {
                    this.fieldConfig.value = this.utilService.formatDate(this.columnData);
                }
            }

            this.tableService.loadColumnPrimitive(this.vcr, this.fieldConfig);
        } else {
            //console.warn("There's no component  for this field info type:", this.columnDef);
        }
    }
}
